import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { clone } from "ramda";
import { useCallback, useContext, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ActionButton,
  ActionButtonSize,
  ColorButton,
  DialogComponent,
  HeaderBar,
  HStack,
  Modal,
  SymbolIcon,
  TooltipIntent,
} from "widgets";
import { useOverlay } from "widgets/hooks";

import { createSelectionModel } from "@/app-utils/selection-model.ts";
import { type CoordinateList, type Organism } from "@/graphql.ts";
import { loadCoordinateLists } from "@/redux/modules/genome-browser.ts";
import {
  createTableSpecForOrganismParent,
  createUniqueKeyFromTableSpec,
  getTableDataSlice,
  type ITableData,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { COLOR } from "@/settings/colors-settings.ts";
import { DEFAULT_COORDINATE_COLOR, ENTITY_TYPE } from "@/settings/settings.ts";
import ColumnSelectionButton from "@/ui-modules/core/ColumnSelectionButton.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";
import { TooltipController } from "@/ui-modules/core/overlays/components/OverlayControllers.tsx";
import { DndActiveContext } from "@/ui-modules/genome-viewer/GenomeViewerInteractiveSurface.tsx";

import { ContentPlaceholder } from "../../core/ContentPlaceholder.js";
import { SortableItem, SortableList } from "../../core/forms/Sortable.js";
import style from "../../core/forms/styles/Sortable.module.scss";

export interface ITrackCoordinateListSelectionProps {
  selectedCoordinateLists: CoordinateListSelection[];
  setSelectedCoordinateLists: (coordinateLists: CoordinateListSelection[]) => void;
  defaultColor: string;
}

export interface CoordinateListSelection {
  coordinateList: CoordinateList;
  color: string;
}

const TrackCoordinateListSelection = (props: ITrackCoordinateListSelectionProps) => {
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome, shallowEqual);
  const dispatch = useDispatch<ReduxDispatch>();
  const { overlayProps, state } = useOverlay({});

  // Disable the track to prevent track movement
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const { setDndActive } = useContext(DndActiveContext);
  useEffect(() => {
    setDndActive(false);
    return () => {
      setDndActive(true);
    };
  }, [setDndActive]);

  const tableSpec = createTableSpecForOrganismParent(ENTITY_TYPE.COORD_LIST, genome.id, null, null);

  const tableData: ITableData = useSelector<IAppState, ITableData>(
    (state) => getTableDataSlice(state.tableData, tableSpec),
    shallowEqual,
  );

  const handleColorModification = useCallback(
    (coordinateListSelection: CoordinateListSelection, color: string) => {
      props.setSelectedCoordinateLists(
        props.selectedCoordinateLists.map((selectedCoordinateList: CoordinateListSelection) => {
          return selectedCoordinateList.coordinateList.id === coordinateListSelection.coordinateList.id
            ? { ...selectedCoordinateList, color: color }
            : selectedCoordinateList;
        }),
      );
    },
    [props.selectedCoordinateLists],
  );

  const handleRemoveCoordinateLists = (removedItem: CoordinateListSelection) => {
    props.setSelectedCoordinateLists(
      props.selectedCoordinateLists.filter((bf) => bf.coordinateList.id !== removedItem.coordinateList.id),
    );
  };

  const handleSelectButton = useCallback(() => {
    const selectedCoordListIds = tableData.selectionModel.selectedIds;
    return dispatch(loadCoordinateLists(selectedCoordListIds as number[])).then((coordinateLists) => {
      const coordListSelection = coordinateLists.map((c) => {
        // preserve existing color selection of the already selected coordinate lists
        const color = props.selectedCoordinateLists.find((s) => s.coordinateList.id === c.id)?.color;
        return { coordinateList: c, color: color ?? DEFAULT_COORDINATE_COLOR } satisfies CoordinateListSelection;
      });
      props.setSelectedCoordinateLists(coordListSelection);
    });
  }, [tableData]);

  return (
    <>
      <HeaderBar
        title="Data Selection"
        actions={[
          <ActionButton
            onPress={() => state.open()}
            size={ActionButtonSize.Small}
            label="Add Coordinate Lists"
            key="track-coordinate-list-data-selection"
          />,
        ]}
      />
      <div>
        {props.selectedCoordinateLists.length === 0 ? (
          <ContentPlaceholder text="No coordinate lists have been selected" />
        ) : (
          <div className="form-group">
            <SortableList
              id="dataseries-list"
              onDragEnd={({ active, over }) => {
                const selectables = clone(props.selectedCoordinateLists);
                props.setSelectedCoordinateLists(
                  arrayMove<CoordinateListSelection>(
                    selectables,
                    active.data.current.sortable.index,
                    over.data.current.sortable.index,
                  ),
                );
              }}
            >
              <SortableContext
                items={props.selectedCoordinateLists.map(
                  (coordinateListSelection) => coordinateListSelection.coordinateList.id,
                )}
                disabled={isColorPickerOpen}
              >
                {props.selectedCoordinateLists.map(
                  (coordinateListSelection: CoordinateListSelection, index: number) => (
                    <SortableItem
                      key={`item-${index}`}
                      index={index}
                      id={coordinateListSelection.coordinateList.id}
                      onRemove={() => handleRemoveCoordinateLists(coordinateListSelection)}
                    >
                      <HStack>
                        {coordinateListSelection.coordinateList.hidden ||
                        coordinateListSelection.coordinateList.deleted ? (
                          <TooltipController
                            content={
                              coordinateListSelection.coordinateList.hidden
                                ? "This coordinate list is not accessible to you."
                                : "This coordinate list has been deleted."
                            }
                            intent={TooltipIntent.Danger}
                          >
                            {(tooltipTriggerProps) => (
                              <SymbolIcon
                                icon={coordinateListSelection.coordinateList.deleted ? "delete" : "security"}
                                style={{ color: COLOR.DARKISH_PINK.BORDER }}
                                size={16}
                                domProps={tooltipTriggerProps}
                              />
                            )}
                          </TooltipController>
                        ) : (
                          <ColorButton
                            defaultColor={coordinateListSelection.color || props.defaultColor}
                            onApply={(color) => handleColorModification(coordinateListSelection, color)}
                            size="tiny"
                          />
                        )}

                        <span className={style.label}>
                          {coordinateListSelection.coordinateList.hidden
                            ? `[Restricted Content]`
                            : coordinateListSelection.coordinateList.name}
                        </span>
                      </HStack>
                    </SortableItem>
                  ),
                )}
              </SortableContext>
            </SortableList>
          </div>
        )}
      </div>
      {state.isOpen && (
        <Modal state={state} {...overlayProps}>
          <DialogComponent
            fullscreen
            title="Select Coordinate Lists"
            mainButton={{
              label: "Select",
              onPress: () => handleSelectButton().then(() => state.close()),
              isDisabled: false,
            }}
            actions={[
              <ColumnSelectionButton
                key="column-selection-button-placeholder"
                portalContext={createUniqueKeyFromTableSpec(tableSpec)}
              />,
            ]}
            closeFn={() => state.close()}
            contentStyle={{ display: "grid", padding: 0, borderTop: "1px solid var(--ui-border)" }}
          >
            <DataTableContainer
              key={createUniqueKeyFromTableSpec(tableSpec)}
              tableSpec={tableSpec}
              gridContext={{ tableSpec: tableSpec }}
              noRowsText="No coordinate lists found"
              tableInitializationOptions={{
                initialSelectionModel: createSelectionModel(
                  props.selectedCoordinateLists.map((c) => c.coordinateList.id),
                ),
              }}
            />
          </DialogComponent>
        </Modal>
      )}
    </>
  );
};

export { TrackCoordinateListSelection };
